import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Card, CardBody, CardHeader, Button, Spinner, Badge } from '../components/index.js';
import { useAuth } from '../lib/auth-context.js';

type HealthState =
  | { kind: 'loading' }
  | { kind: 'ok'; version: string; uptime: number }
  | { kind: 'error'; message: string };

export function Home() {
  const { user, loading: authLoading } = useAuth();
  const [health, setHealth] = useState<HealthState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetch('/api/health')
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { ok: boolean; version: string; uptime: number };
        if (!cancelled) setHealth({ kind: 'ok', version: body.version, uptime: body.uptime });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          setHealth({ kind: 'error', message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!authLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-xl">
        <div className="mb-8">
          <Badge variant="accent">Phase 2 · Design system online</Badge>
        </div>
        <h1 className="text-4xl font-bold tracking-tight">TAS Portal</h1>
        <p className="mt-3 text-lg text-text-secondary">
          Sign in to access your tools, jobs, and team settings.
        </p>

        <Card className="mt-8">
          <CardHeader title="API health" description="Live status of the Fastify backend" />
          <CardBody>
            {health.kind === 'loading' && (
              <p data-testid="health-status" className="flex items-center gap-2 text-text-secondary">
                <Spinner size="sm" />
                Checking API…
              </p>
            )}
            {health.kind === 'ok' && (
              <p data-testid="health-status" className="flex items-center gap-2 text-text-primary">
                <CheckCircle2 className="size-5 text-success" aria-hidden />
                <span>
                  API connected — <span className="font-mono text-sm">v{health.version}</span> (uptime {health.uptime.toFixed(1)}s)
                </span>
              </p>
            )}
            {health.kind === 'error' && (
              <p data-testid="health-status" className="flex items-center gap-2 text-danger">
                <AlertCircle className="size-5" aria-hidden />
                API unreachable: {health.message}
              </p>
            )}
          </CardBody>
        </Card>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/login">
            <Button rightIcon={<ArrowRight className="size-4" aria-hidden />}>Sign in</Button>
          </Link>
          <Link to="/signup">
            <Button variant="secondary">Create account</Button>
          </Link>
          <Link to="/internal/design">
            <Button variant="ghost">Design system</Button>
          </Link>
        </div>
      </div>
    </main>
  );
}
